import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { BarChart3, Loader2, Edit3, Check, TrendingUp, DollarSign, AlertCircle, Sparkles, Bell, Library } from 'lucide-react';
import { api } from '../../lib/api';
import { useWizardStore, type IndustryData } from '../../stores/wizardStore';
import { useResearchJobsStore } from '../../stores/researchJobsStore';
import { useResearchJobProgress, formatElapsed } from '../../hooks/useResearchJobProgress';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import { toast } from 'sonner';
import IndustryInsightsPanel from '../../components/wizard/IndustryInsightsPanel';
import { formatCurrencyValue, regionFromCountry } from '../../components/financial/utils/formatters';

interface Step2Props {
  onNext: () => void;
  onBack: () => void;
}

type SmartIndustryResponse =
  | {
      status: 'completed';
      industry: IndustryData & { industryId?: string };
      industryId?: string;
      currentVersionId?: string;
      source: 'existing' | 'generated_comprehensive';
    }
  | {
      status: 'running';
      jobId: string;
      industryId?: string;
      message?: string;
    };

/**
 * Render a percentage value with at most 2 decimal places. Strips trailing
 * zeros so "25%" stays "25%" while "26.78887727162" renders as "26.79%".
 * Returns "—" for null/undefined/NaN.
 */
function formatPercent(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '—';
  // Round to 2 decimals, then strip trailing zeros.
  const rounded = Math.round(num * 100) / 100;
  return `${rounded}%`;
}

export const Step2IndustryResearch: React.FC<Step2Props> = ({ onNext, onBack }) => {
  const navigate = useNavigate();
  const { companyData, industryData, setIndustryData } = useWizardStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Partial<IndustryData> | null>(null);
  const [researchSource, setResearchSource] = useState<'existing' | 'generated_comprehensive' | null>(null);

  // Watch researchJobsStore for an active industry research job belonging to
  // *this* company. Filtering by companyName is critical: the store is global
  // and persists across wizard sessions, so without this scope an in-flight
  // job from a previous case (e.g. Company A's FMCG research) would bleed
  // into a brand-new wizard for Company B — Step 2 would render the spinner
  // and the silent-lookup useEffect (below) would skip its existence check,
  // preventing the wizard from detecting Company B's already-cached research.
  const currentCompanyName = companyData?.name;
  const activeIndustryJob = useResearchJobsStore((s) =>
    Object.values(s.jobs).find(
      (j) =>
        j.kind === 'industry_research' &&
        j.status === 'running' &&
        // Match on companyName since that's what registerJob stores. Both
        // sides are normalized so casing/whitespace differences don't cause
        // a false miss (a real miss would be worse than the cross-contamination
        // we're trying to avoid — it would re-trigger the long-running job).
        (j.companyName ?? '').trim().toLowerCase() ===
          (currentCompanyName ?? '').trim().toLowerCase(),
    ),
  );
  // The same scoping, for a job that FAILED. `activeIndustryJob` filters on
  // status === 'running', so without this a failure simply made the progress
  // panel disappear — the user was dropped back to the start form with no
  // indication anything had gone wrong. Only unnotified failures are surfaced
  // so a retry (which leaves the old failed job in the store) doesn't get
  // stuck behind the previous attempt's error screen.
  const failedIndustryJob = useResearchJobsStore((s) =>
    Object.values(s.jobs).find(
      (j) =>
        j.kind === 'industry_research' &&
        j.status === 'failed' &&
        !j.dismissed &&
        (j.companyName ?? '').trim().toLowerCase() ===
          (currentCompanyName ?? '').trim().toLowerCase(),
    ),
  );
  const registerJob = useResearchJobsStore((s) => s.registerJob);
  const updateJob = useResearchJobsStore((s) => s.updateJob);

  // Elapsed / stall / timeout presentation state for the running job.
  const {
    elapsedSeconds,
    isStalled,
    isTimedOut: jobTimedOut,
    isSlowerThanTypical,
  } = useResearchJobProgress(activeIndustryJob);

  // Retry: dismiss the previous attempt's error screen, then re-run.
  const handleRetryResearch = () => {
    if (failedIndustryJob) {
      updateJob(failedIndustryJob.jobId, { dismissed: true });
    }
    if (jobTimedOut && activeIndustryJob) {
      updateJob(activeIndustryJob.jobId, {
        status: 'failed',
        dismissed: true,
        error: 'Timed out after 5 minutes.',
      });
    }
    generateIndustryResearch.mutate(true);
  };

  // Initialize edited data when industry data exists
  useEffect(() => {
    if (industryData) {
      setEditedData(industryData);
    }
  }, [industryData]);

  // Identity key for the company. When this changes (user switched cases or
  // picked a different company), the silent-lookup guard must reset so a
  // fresh `/industry-research/smart` call fires for the new company instead
  // of reusing the previous case's industryData.
  const companyKey =
    companyData?.companyId ||
    companyData?.cik ||
    companyData?.scId ||
    companyData?.companyNumber ||
    companyData?.name?.toLowerCase() ||
    null;
  // Track which company we've already probed so re-renders don't fire it twice
  // but a company switch DOES re-fire it.
  const [checkedForCompany, setCheckedForCompany] = useState<string | null>(null);
  const hasCheckedLibrary = checkedForCompany !== null && checkedForCompany === companyKey;
  const [isCheckingLibrary, setIsCheckingLibrary] = useState(false);

  // On mount: silently check whether this company's industry already has
  // comprehensive research in the library. If yes, auto-populate. If no,
  // fall back to the empty state so the user can decide to Generate or
  // Enter Manually — we never trigger a long-running job behind their back.
  useEffect(() => {
    if (hasCheckedLibrary || industryData || !companyData || activeIndustryJob) {
      return;
    }

    let cancelled = false;
    setIsCheckingLibrary(true);
    setCheckedForCompany(companyKey);

    let cik: string | undefined = companyData.cik;
    if (!cik && companyData.companyId?.startsWith('sec_')) {
      cik = companyData.companyId.replace(/^sec_/, '');
    }

    api
      .post<SmartIndustryResponse | { status: 'not_found' }>(
        '/case-discovery/industry-research/smart',
        {
          companyName: companyData.name,
          industryHint: companyData.industry,
          cik,
          country: companyData.country,
          lookupOnly: true,
          createdBy: 'case_wizard',
        },
      )
      .then((response) => {
        if (cancelled) return;
        if (response.status === 'completed') {
          setIndustryData({
            ...response.industry,
            industryId: response.industryId ?? response.industry.industryId,
          } as IndustryData);
          setEditedData(response.industry as IndustryData);
          setResearchSource(response.source);
          toast.success('Loaded comprehensive industry research from library');
        }
        // 'not_found' — leave the empty state alone; user clicks Generate
      })
      .catch((err) => {
        console.error('Industry research lookup failed:', err);
        // Silent fail — user can still click Generate manually
      })
      .finally(() => {
        if (!cancelled) setIsCheckingLibrary(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData?.companyId, companyData?.industry, companyData?.name]);

  // Smart industry research mutation
  const generateIndustryResearch = useMutation({
    mutationFn: async (forceRefresh: boolean = false) => {
      if (!companyData) throw new Error('Company data required');

      // Prefer the structured CIK field; fall back to parsing the legacy
      // companyId format `sec_<cik>` for cases loaded from older stored state.
      let cik: string | undefined = companyData.cik;
      if (!cik && companyData.companyId?.startsWith('sec_')) {
        cik = companyData.companyId.replace(/^sec_/, '');
      }

      return api.post<SmartIndustryResponse>(
        '/case-discovery/industry-research/smart',
        {
          companyName: companyData.name,
          industryHint: companyData.industry,
          cik,
          country: companyData.country,
          forceRefresh,
          createdBy: 'case_wizard',
        },
      );
    },
    onSuccess: (response) => {
      if (response.status === 'completed') {
        setIndustryData({
          ...response.industry,
          industryId: response.industryId ?? response.industry.industryId,
        } as IndustryData);
        setEditedData(response.industry as IndustryData);
        setResearchSource(response.source);
        setIsEditing(false);
        toast.success(
          response.source === 'existing'
            ? 'Loaded comprehensive industry research from library'
            : 'Industry research generated',
        );
      } else {
        // status === 'running' — register in jobs store and let the notifier handle the rest
        registerJob({
          jobId: response.jobId,
          kind: 'industry_research',
          label: `Industry research for ${companyData?.name ?? 'company'}`,
          companyName: companyData?.name,
          industryName: companyData?.industry,
          resumePath: '/cases/new-wizard',
        });
        toast.info(response.message ?? 'Generating comprehensive industry research in the background.', {
          duration: 6000,
        });
      }
    },
    onError: (error: any) => {
      console.error('Industry research failed:', error);
      toast.error(`Industry research failed: ${error.message || 'Unknown error'}`);
    },
  });

  const handleGenerateWithAI = (forceRefresh: boolean = false) => {
    generateIndustryResearch.mutate(forceRefresh);
  };

  const handleSaveEdits = () => {
    if (editedData) {
      setIndustryData(editedData as IndustryData);
      setIsEditing(false);
    }
  };

  const handleCancelEdits = () => {
    setEditedData(industryData);
    setIsEditing(false);
  };

  const updateEditedField = (field: keyof IndustryData, value: any) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const displayData = editedData || industryData;
  const isGenerating = generateIndustryResearch.isPending || !!activeIndustryJob;

  // Handle manual entry mode when editing
  const handleStartManualEntry = () => {
    const initialData: IndustryData = {
      name: companyData?.industry || '',
      classification: companyData?.industry || '',
      marketSizeUSD: 0,
      marketGrowthRate: 0,
      valueChain: [],
      technologyTrends: [],
      regulatoryEnvironment: '',
      commonPainPoints: [],
      benchmarks: { gross_margin: 0, operating_margin: 0, roe: 0, revenue_growth: 0 },
    };
    setEditedData(initialData);
    setIsEditing(true);
  };

  // A job that FAILED (or timed out) needs its own terminal screen — the
  // spinner is indistinguishable from a slow run otherwise. Note this reads
  // `failedIndustryJob`, a separate selector: `activeIndustryJob` filters on
  // status === 'running', so a failed job would simply make this panel vanish
  // and drop the user back to the start form with no explanation.
  if ((failedIndustryJob || jobTimedOut) && !industryData) {
    const failureMessage = jobTimedOut
      ? 'The research job has been running for over 5 minutes without completing. It may still finish in the background, but something is likely wrong.'
      : failedIndustryJob?.error || 'The research job did not complete.';

    return (
      <div className="max-w-4xl mx-auto">
        <Card className="p-12">
          <div className="text-center space-y-6">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500" />
            <div>
              <h3 className="text-2xl font-semibold text-neutral-900 mb-2">
                {jobTimedOut ? 'Industry research is taking too long' : 'Industry research failed'}
              </h3>
              <p className="text-neutral-600 max-w-lg mx-auto">{failureMessage}</p>
            </div>

            <div className="flex items-center justify-center gap-3">
              <Button variant="primary" onClick={handleRetryResearch}>
                <Sparkles className="w-4 h-4 mr-2" />
                Try again
              </Button>
              <Button variant="outline" onClick={handleStartManualEntry}>
                <Edit3 className="w-4 h-4 mr-2" />
                Enter manually
              </Button>
            </div>
          </div>
        </Card>

        <div className="flex justify-between pt-6 border-t border-neutral-200 mt-6">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  // While a background job is running and no data yet, show progress screen
  if (activeIndustryJob && !industryData) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="p-12">
          <div className="text-center space-y-6">
            <Loader2 className="w-16 h-16 mx-auto text-primary-600 animate-spin" />
            <div>
              <h3 className="text-2xl font-semibold text-neutral-900 mb-2">
                Generating comprehensive industry research…
              </h3>
              {/* The backend's `message` is more informative than the number —
                  it names the actual step, and it changes even when progress
                  doesn't. Elapsed time appears once the run is long enough for
                  the question "is this stuck?" to arise. */}
              <p className="text-neutral-600">
                {activeIndustryJob.message || 'Working on market data, competitive landscape, and trends.'}
                {elapsedSeconds > 15 && (
                  <span className="text-neutral-400">
                    {' '}· {formatElapsed(elapsedSeconds)} elapsed
                  </span>
                )}
              </p>
              {/* "Typical run time: 30–60 seconds" becomes actively misleading
                  at three minutes, so it is replaced once we pass it. */}
              <p className="text-sm text-neutral-500 mt-3">
                {isSlowerThanTypical
                  ? 'Taking longer than usual — the language model is still responding. Feel free to switch to another tab; we\'ll notify you when it\'s ready.'
                  : 'Typical run time: 30–60 seconds. Feel free to switch to another tab — we\'ll notify you when it\'s ready.'}
              </p>
            </div>

            {/* Progress bar.
                The backend's checkpoints are unevenly spaced (3/5/15/30/85/100)
                and everything between 30 and 85 is one LLM call that cannot
                report from inside itself. Once the number stops moving we swap
                to an indeterminate bar: "working, duration unknown" is honest,
                whereas a static 30% reads as "stuck". */}
            <div className="w-full max-w-md mx-auto">
              <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                {isStalled ? (
                  <div className="h-full w-1/3 bg-primary-500 rounded-full animate-global-loading-bar" />
                ) : (
                  <div
                    className="h-full bg-primary-500 transition-all duration-500"
                    style={{ width: `${activeIndustryJob.progress}%` }}
                  />
                )}
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                {isStalled ? 'Working…' : `${activeIndustryJob.progress}%`}
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 max-w-md mx-auto">
              <Bell className="w-4 h-4" />
              <span className="text-sm">You'll get a notification when the research is ready.</span>
            </div>
          </div>
        </Card>

        {/* Navigation buttons available so user isn't trapped */}
        <div className="flex justify-between pt-6 border-t border-neutral-200 mt-6">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button variant="outline" onClick={() => navigate('/case-library')}>
            Leave wizard
          </Button>
        </div>
      </div>
    );
  }

  // Show empty state with AI generation option when no data exists
  if (!displayData && !isEditing) {
    // Brief lookup banner while we check the library
    if (isCheckingLibrary) {
      return (
        <div className="max-w-4xl mx-auto">
          <Card className="p-12">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 mx-auto text-primary-600 animate-spin" />
              <h3 className="text-xl font-semibold text-neutral-900">
                Checking Industry Research library for {companyData?.name}…
              </h3>
              <p className="text-sm text-neutral-500">
                If we've already researched this industry, we'll reuse it instead of generating new analysis.
              </p>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-neutral-900">Industry & Market Context</h2>
          <p className="text-neutral-600">
            Generate comprehensive industry research or enter details manually for {companyData?.name || 'your company'}
          </p>
        </div>

        <Card className="p-12 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="w-20 h-20 mx-auto bg-primary-100 rounded-full flex items-center justify-center">
              <BarChart3 className="w-10 h-10 text-primary-600" />
            </div>

            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-neutral-900">No Industry Data Yet</h3>
              <p className="text-neutral-600 leading-relaxed">
                We'll first check the Industry Research library for{' '}
                <span className="font-semibold">{companyData?.industry || companyData?.name}</span>.
                If not found, we'll generate comprehensive research in the background (30–60s) and notify you when ready.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleGenerateWithAI(false)}
                disabled={isGenerating || !companyData}
                className="flex items-center gap-2 min-w-[260px]"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Industry Research
                  </>
                )}
              </Button>

              <span className="text-neutral-400 font-medium">or</span>

              <Button
                variant="outline"
                size="lg"
                onClick={handleStartManualEntry}
                disabled={isGenerating}
                className="flex items-center gap-2 min-w-[200px]"
              >
                <Edit3 className="w-5 h-5" />
                Enter Manually
              </Button>
            </div>

            <div className="pt-6 border-t border-neutral-200">
              <p className="text-sm text-neutral-500">
                ✨ Comprehensive research includes: market size & growth • TAM/SAM/SOM • competitive landscape • technology trends • disruptions • executive summary
              </p>
            </div>
          </div>
        </Card>

        <div className="flex justify-between pt-6 border-t border-neutral-200">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button variant="primary" onClick={onNext} disabled={true} className="flex items-center gap-2">
            Next: Competition Analysis
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-neutral-900">Industry & Market Context</h2>
        <p className="text-neutral-600">
          Comprehensive industry research for {companyData?.name || 'your company'}
        </p>
      </div>

      {/* Source banner — visible when data came from the standalone library */}
      {researchSource === 'existing' && industryData?.industryId && (
        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Library className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-900">
              Linked to Industry Research library
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              Reusing existing research record. Click "Regenerate" to refresh from a new LLM run.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => handleGenerateWithAI(true)} disabled={isGenerating}>
            Refresh
          </Button>
        </div>
      )}
      {researchSource === 'generated_comprehensive' && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-900">
            Freshly generated comprehensive research. Saved to the Industry Research library for reuse.
          </p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          {!isEditing ? (
            <>
              <Button
                variant="primary"
                onClick={() => handleGenerateWithAI(true)}
                disabled={isGenerating || !companyData}
                className="flex items-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Regenerate
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleStartManualEntry} disabled={isGenerating} className="flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                Edit Manually
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" onClick={handleSaveEdits} className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Save Changes
              </Button>
              <Button variant="outline" onClick={handleCancelEdits}>
                Cancel
              </Button>
            </>
          )}
        </div>

        <div className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${
          industryData && !isEditing ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
        }`}>
          {industryData && !isEditing ? (
            <>
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Data Ready</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">
                {isEditing ? 'Editing Mode' : 'Generate or Enter Manually'}
              </span>
            </>
          )}
        </div>
      </div>

      <Card className="p-6 bg-gradient-to-br from-primary-50 to-white border-2 border-primary-200">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">Industry</label>
            {isEditing ? (
              <Input value={editedData?.name || ''} onChange={(e) => updateEditedField('name', e.target.value)} />
            ) : (
              <p className="text-xl font-semibold text-neutral-900">{displayData?.name}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">Classification</label>
            {isEditing ? (
              <Input value={editedData?.classification || ''} onChange={(e) => updateEditedField('classification', e.target.value)} />
            ) : (
              <p className="text-xl font-semibold text-neutral-900">{displayData?.classification}</p>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary-600" />
          Market Context
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            {(() => {
              // Market size: when the backend resolved a country-specific
              // breakdown (marketScope === "country"), label as e.g. "Market
              // Size (India)" and render with the selected country's currency.
              // Otherwise fall back to the global TAM with a "Global" label.
              const scope = (displayData as any)?.marketScope;
              const isCountrySpecific = scope === 'country';
              const country = companyData?.country;
              const region = isCountrySpecific ? regionFromCountry(country) : undefined;
              const sizeRaw = displayData?.marketSizeUSD ?? 0;
              const sizeLabel = isCountrySpecific
                ? `Market Size (${country ?? 'Region'})`
                : 'Market Size (Global)';
              const sizeHint = isCountrySpecific
                ? `Total addressable market for ${country ?? 'region'}`
                : 'Total addressable market — global';
              const formatted = region
                ? formatCurrencyValue(sizeRaw, region, /* compact */ true)
                : `$${(sizeRaw / 1e12).toFixed(2)}T`;
              return (
                <>
                  <label className="block text-sm font-medium text-neutral-600 mb-2">
                    {sizeLabel}
                  </label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={editedData?.marketSizeUSD || 0}
                      onChange={(e) => updateEditedField('marketSizeUSD', parseFloat(e.target.value))}
                    />
                  ) : (
                    <p className="text-2xl font-bold text-primary-600">{formatted}</p>
                  )}
                  <p className="text-xs text-neutral-500 mt-1">{sizeHint}</p>
                </>
              );
            })()}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">Market Growth Rate</label>
            {isEditing ? (
              <Input
                type="number"
                step="0.1"
                value={editedData?.marketGrowthRate || 0}
                onChange={(e) => updateEditedField('marketGrowthRate', parseFloat(e.target.value))}
              />
            ) : (
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-green-600">{displayData?.marketGrowthRate}%</p>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            )}
            <p className="text-xs text-neutral-500 mt-1">CAGR</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4">Key Technology Trends</h3>
        {isEditing ? (
          <div className="space-y-2">
            {(editedData?.technologyTrends || []).map((trend, idx) => (
              <Input
                key={idx}
                value={trend}
                onChange={(e) => {
                  const newTrends = [...(editedData?.technologyTrends || [])];
                  newTrends[idx] = e.target.value;
                  updateEditedField('technologyTrends', newTrends);
                }}
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {displayData?.technologyTrends?.map((trend, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2" />
                <span className="text-neutral-700">{trend}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4">Industry Benchmarks</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-neutral-50 rounded-lg">
            <p className="text-xs text-neutral-600 mb-1">Gross Margin</p>
            <p className="text-2xl font-bold text-neutral-900">{formatPercent(displayData?.benchmarks?.gross_margin)}</p>
          </div>
          <div className="p-4 bg-neutral-50 rounded-lg">
            <p className="text-xs text-neutral-600 mb-1">Operating Margin</p>
            <p className="text-2xl font-bold text-neutral-900">{formatPercent(displayData?.benchmarks?.operating_margin)}</p>
          </div>
          <div className="p-4 bg-neutral-50 rounded-lg">
            <p className="text-xs text-neutral-600 mb-1">ROE</p>
            <p className="text-2xl font-bold text-neutral-900">{formatPercent(displayData?.benchmarks?.roe)}</p>
          </div>
          <div className="p-4 bg-neutral-50 rounded-lg">
            <p className="text-xs text-neutral-600 mb-1">Revenue Growth</p>
            <p className="text-2xl font-bold text-neutral-900">{formatPercent(displayData?.benchmarks?.revenue_growth)}</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4">Value Chain</h3>
        <div className="flex flex-wrap gap-3">
          {displayData?.valueChain?.map((stage, idx) => (
            <div
              key={idx}
              className="px-4 py-2 bg-primary-50 border border-primary-200 rounded-lg text-sm font-medium text-primary-700"
            >
              {stage}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4">Common Industry Pain Points</h3>
        <ul className="space-y-2">
          {displayData?.commonPainPoints?.map((pain, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <span className="text-neutral-700">{pain}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4">Regulatory Environment</h3>
        {isEditing ? (
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={4}
            value={editedData?.regulatoryEnvironment || ''}
            onChange={(e) => updateEditedField('regulatoryEnvironment', e.target.value)}
          />
        ) : (
          <p className="text-neutral-700 leading-relaxed">{displayData?.regulatoryEnvironment}</p>
        )}
      </Card>

      {/* Industry insights — three collapsible sections surfacing rich
          IndustryResearch content (executive summary, disruptions, tech trends)
          that the flat wizard IndustryData shape doesn't include. Read-only;
          fetches from /case-discovery/industry-insights/{industryId}. */}
      {industryData?.industryId && (
        <IndustryInsightsPanel industryId={industryData.industryId} />
      )}

      <div className="flex justify-between pt-6 border-t border-neutral-200">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button variant="primary" onClick={onNext} disabled={!industryData} className="flex items-center gap-2">
          Next: Competition Analysis
        </Button>
      </div>
    </div>
  );
};

export default Step2IndustryResearch;
